import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { espApi } from '@/store/esp/api';
import type { EspExternalUserLadder, EspInternalUserLadder, EspStreamPriced } from '@/store/esp/state.ts';
import { getEspStreamId, initEspState } from '@/store/esp/utils.ts';

export type EspPrice = {
  streamId: string;
  quoteId: string;
  multipassInstanceId: string;
  midPrice: number;
  precision: number;
  valid: boolean;
  // priceDecodedTime: number;
  frontTimestamp: number;
  backtimestamp: number;
} & (
  | { priceType: 'ESP.TRADER.PRICE'; ladders: EspInternalUserLadder[] }
  | { priceType: 'ESP.CLIENT.PRICE'; ladders: EspExternalUserLadder[] }
);

export type EspHeartbeat = {
  priceType: 'ESP.HEARTBEAT';
  streamId: string;
  lastPriceWhen: string;
  timestamp: number;
};

export const espSlice = createSlice({
  name: 'esp',

  initialState: initEspState(),

  reducers: {
    espStreamSubscribed: (
      state,
      action: PayloadAction<{
        currencyPair: string;
        tiering: string;
        tileId: string;
        bdrId: number;
      }>,
    ) => {
      // bdrId is the companyId, used to request ESP

      const espId = getEspStreamId(action.payload.currencyPair, action.payload.tiering);

      const currentEspStream = state.streams[espId];

      if (isDefined(currentEspStream)) {
        if (currentEspStream.subscribedTileIds.includes(action.payload.tileId)) {
          return;
        }

        currentEspStream.subscribedTileIds.push(action.payload.tileId);
      } else {
        state.streams[espId] = {
          currencyPair: action.payload.currencyPair,
          tiering: action.payload.tiering,
          status: 'PENDING',
          subscribedTileIds: [action.payload.tileId],
        };
      }
    },

    espStreamUnsubscribed: (
      state,
      action: PayloadAction<{ currencyPair: string; tiering: string; tileId: string }>,
    ) => {
      const espId = getEspStreamId(action.payload.currencyPair, action.payload.tiering);

      const currentEspStream = state.streams[espId];

      if (isNotDefined(currentEspStream)) {
        return;
      }

      currentEspStream.subscribedTileIds = currentEspStream.subscribedTileIds.filter(
        (tileId) => tileId !== action.payload.tileId,
      );
    },

    espStreamTimeout: (state, action: PayloadAction<{ streamId: string }>) => {
      const currentEspStream = state.streams[action.payload.streamId];

      if (isNotDefined(currentEspStream)) {
        return;
      }

      state.streams[action.payload.streamId] = {
        currencyPair: currentEspStream.currencyPair,
        tiering: currentEspStream.tiering,
        status: 'PENDING',
        subscribedTileIds: currentEspStream.subscribedTileIds,
      };
    },

    espStreamCancel: (state, action: PayloadAction<{ currencyPair: string; tiering: string }>) => {
      const espId = getEspStreamId(action.payload.currencyPair, action.payload.tiering);

      const currentEspStream = state.streams[espId];

      if (isNotDefined(currentEspStream)) {
        return;
      }

      delete state.streams[espId];
    },

    espPriceReceived: (state, action: PayloadAction<EspPrice>) => {
      const currentEspStream = state.streams[action.payload.streamId];

      if (isNotDefined(currentEspStream)) {
        return;
      }

      // Isolating priceType/ladders via rest destructuring (instead of reading them separately from
      // action.payload) keeps TypeScript's discriminated-union correlation between the two fields.
      const {
        streamId,
        quoteId,
        multipassInstanceId,
        midPrice,
        precision,
        valid,
        frontTimestamp,
        backtimestamp,
        ...priceTypeAndLadders
      } = action.payload;

      state.streams[streamId] = {
        currencyPair: currentEspStream.currencyPair,
        currencyPairPrecision: precision,
        tiering: currentEspStream.tiering,
        status: 'PRICED',
        subscribedTileIds: currentEspStream.subscribedTileIds,

        quoteId,
        multipassInstanceId,

        midRate: midPrice,
        valid,

        // priceDecodedTime: action.payload.priceDecodedTime,
        frontTimestamp,
        backTimestamp: backtimestamp,

        ...priceTypeAndLadders,
      } satisfies EspStreamPriced;
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(espApi.endpoints.getEspLimits.matchFulfilled, (state, action) => {
      const limits = action.payload;

      if (isNotDefined(limits)) {
        return;
      }

      for (const [currency, limit] of Object.entries(limits)) {
        state.limits[currency] = limit;
      }
    });
  },

  selectors: {
    selectCurrenciesWithEspLimits: createSelector(
      (state) => state.limits,
      (limits) => Object.keys(limits),
    ),
    selectEspStreamStatus: createSelector(
      (state, espStreamId: string) => state.streams[espStreamId],
      (stream) => {
        if (!stream) {
          return null;
        }
        return stream.status;
      },
    ),
  },
});

export const { espStreamSubscribed, espStreamUnsubscribed, espPriceReceived, espStreamTimeout, espStreamCancel } =
  espSlice.actions;
